import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map, switchMap, take, withLatestFrom } from 'rxjs/operators';
import {
  CompanyParticipantsIds,
  createProfileReportFinished,
  openParticipantProfileReportDrawer,
  openProfileReportDrawer
} from '@capital-access/common/actions';
import { getFeatureToggle } from '@capital-access/common/feature-toggle';
import { LocalizationService } from '@capital-access/common/localization';
import { getSelectedRelatedSecurities } from '@capital-access/common/security';
import {
  DrawerConfig,
  DrawerOutput,
  DrawerPosition,
  DrawerRef,
  DrawerType,
  FireflyDrawerService
} from '@capital-access/firefly/components';
import { ProfileType } from '@capital-access/profiles/common';
import { TemplateLibraryService } from '@capital-access/reporting/common';
import { LOCALIZATION_KEY } from '../../constants';
import { SectionSelectorListItem } from '../../core/models/report-sections';
import { getValidConfigurableLayout } from '../../core/utils/configurable-template.util';
import { getSavedSections } from '../../core/utils/section-settings.utils';
import { ProfileReportDrawerComponent } from '../components/profile-report-drawer/profile-report-drawer.component';
import { CreateProfileReportDto } from '../models/profile-report.models';
import {
  getConditionalReportSections,
  getDefaultSections,
  ProfileReportLayout
} from '../models/profile-report-sections';
import { SortingDirectionService } from '../services/sorting-direction.service';

@Injectable()
export class ProfileReportDrawerEffects {
  openDrawer$ = createEffect(() => this.actions$.pipe(ofType(openProfileReportDrawer), this.openDrawer()));

  openParticipantDrawer$ = createEffect(() =>
    this.actions$.pipe(ofType(openParticipantProfileReportDrawer), this.openDrawer(true))
  );

  private openDrawer(participantReport = false) {
    return (
      source: Observable<{
        profileType: ProfileType;
        profiles: { prId?: number | null; crmId?: number | null; name?: string | null }[];
        combinedSecurityIds?: number[];
        includedContacts?: CompanyParticipantsIds[];
        reportedQuarters?: string[];
        moduleName?: string;
      }>
    ) =>
      source.pipe(
        withLatestFrom(
          this.store.select(getSelectedRelatedSecurities),
          this.localization.getLocalization(LOCALIZATION_KEY),
          this.store.select(getFeatureToggle('profile-report-historical-ownership-SKYL-2377')),
          this.store.select(getFeatureToggle('profile-report-sustainability-fit-ISSYN-2489')),
          this.store.select(getFeatureToggle('add-notes-section-in-investor-profile-report-ISD-17482')),
          this.store.select(getFeatureToggle('ai-summary-card-isd-4584'))
        ),
        switchMap(
          ([
            action,
            relatedSecurities,
            localization,
            isHistoricalOwnershipToggleEnabled,
            isSustainabilityFitToggleEnabled,
            isNotesFeatureEnabled,
            isAiSummaryCardEnabled
          ]) => {
            const conditionalSections = getConditionalReportSections({
              historicalOwnership: action.profileType !== ProfileType.Contact || isHistoricalOwnershipToggleEnabled,
              sustainabilityFitMetric:
                action.profileType === ProfileType.Institution && isSustainabilityFitToggleEnabled,
              primaryContactBiography: !participantReport && action.profileType === ProfileType.Institution,
              participantBiography: participantReport && action.profileType === ProfileType.Institution,
              combinedOwnership: action.profileType === ProfileType.Institution && relatedSecurities?.length >= 2
            });

            const request = CreateProfileReportDto.create({
              profiles: action.profiles,
              profileType: action.profileType,
              includedContacts: action.includedContacts,
              combinedSecurityIds: action.combinedSecurityIds || relatedSecurities.map(x => x.id),
              reportedQuarters: action.reportedQuarters,
              moduleName: action.moduleName,
              sortDirection: this.sortingDirectionService.getSortDirection()
            });
            const filteredSections = getSavedSections(
              action.profileType,
              getDefaultSections(
                action.profileType as ProfileType,
                ProfileReportLayout.Detailed,
                isNotesFeatureEnabled,
                isAiSummaryCardEnabled
              ),
              conditionalSections
            );

            const drawerConfig = {
              title: localization[`${action.profileType}DrawerHeader`],
              position: DrawerPosition.Right,
              type: DrawerType.Form,
              dataLayoutId: 'modify-profile-report-drawer'
            };

            const onePagerSections = getSavedSections(
              `${action.profileType}${ProfileReportLayout.OnePager}`,
              getDefaultSections(
                action.profileType,
                ProfileReportLayout.OnePager,
                isNotesFeatureEnabled,
                isAiSummaryCardEnabled
              ),
              conditionalSections
            );

            return getValidConfigurableLayout(this.store, this.templateService, action.profileType).pipe(
              switchMap(layout => {
                const drawer: DrawerRef = this.buildProfileDrawer(
                  filteredSections,
                  onePagerSections,
                  request,
                  drawerConfig,
                  action.profileType,
                  layout
                );
                return drawer.onClose().pipe(
                  take(1),
                  filter((output: DrawerOutput) => !output || !output['reportInProgress']),
                  map(() => createProfileReportFinished())
                );
              })
            );
          }
        )
      );
  }

  private buildProfileDrawer(
    filteredSections: SectionSelectorListItem[],
    onePagerSections: SectionSelectorListItem[],
    request: CreateProfileReportDto,
    drawerConfigs: DrawerConfig,
    profileType: ProfileType,
    layout: ProfileReportLayout
  ) {
    return this.drawerService.openDrawer(drawerConfigs, ProfileReportDrawerComponent, {
      request,
      filteredSections: {
        [ProfileReportLayout.Detailed]: filteredSections,
        [ProfileReportLayout.OnePager]: onePagerSections
      },
      layout: layout as ProfileReportLayout
    });
  }

  constructor(
    private actions$: Actions,
    private localization: LocalizationService,
    private drawerService: FireflyDrawerService,
    private store: Store,
    private sortingDirectionService: SortingDirectionService,
    private templateService: TemplateLibraryService
  ) {}
}
